import React from 'react';
import { ScrollView, View } from 'react-native';

import tw from '@/styles/tailwind';
import Text from '@/components/Text';

// ───────────────────────────────────────────────────────────────────────────
// Reporte "General de ingresos — detallado mensual" (por comunidad, por mes).
// Es la contraparte de ingresos de los reportes de egresos: una matriz
//   concepto de ingreso (filas) × (semana × {Tarjeta, Depósito}) + TOTAL,
// en dos secciones (Mantenimiento / Fondo de Reserva) con subtotal, gran total
// (Ingresos reportados por cobranza) y dos filas de pie.
//
// Las semanas arrancan en DOMINGO. SEM 01 puede ser parcial (en mayo 2026,
// el 1 cayó viernes → SEM 01 = 1–2, SEM 02 = 3–9, etc.).
//
// Mapeo backend (reusa lo ya definido; aún sin endpoint):
//  • Origen        → FeePayment (ingresos).
//  • Semana        → ISO-ish week de apply_date (arranca domingo).
//  • Tarjeta/Depósito → payment_method colapsado en 2 buckets.
//  • Mtto del mes / anteriores / futuros → split por FeeUnit.apply_month vs mes (M/R/A).
//  • Aportación por Vinte → pagos de unidades en inventario Vinte.
//  • Monto por sanción    → surcharge (recargos) cobrado.
//  • Monto no identificada→ depósitos al banco SIN referencia (no se sabe la vivienda).
//  • Viviendas entregadas y con aportación → entregadas en la semana que pagaron su
//      Fondo de Reserva (cada FR = $1,800), contadas por tarjeta / depósito.
// Por investigar (cliente): efectivo en tránsito, sanciones, sanción de no identificados.
// El "concepto" es un CLASIFICADOR por reglas sobre cada FeePayment (no un group-by simple).
// TODO(backend): reemplazar DEMO por useQuery(communityId, mes).
// ───────────────────────────────────────────────────────────────────────────

type WeekVal = { tarjeta: number; deposito: number };
type Concepto = { nombre: string; semanas: WeekVal[] };

const COMMUNITY = 'Real Bilbao';
const PERIODO = 'Mayo 2026';
const SEMANAS = [
  { label: 'SEM 01', rango: '1–2' },
  { label: 'SEM 02', rango: '3–9' },
  { label: 'SEM 03', rango: '10–16' },
  { label: 'SEM 04', rango: '17–23' },
  { label: 'SEM 05', rango: '24–31' },
];

const w = (tarjeta: number, deposito: number): WeekVal => ({ tarjeta, deposito });

// ─── Datos DEMO (Mayo 2026) ──────────────────────────────────────────────────
const MANTENIMIENTO: Concepto[] = [
  { nombre: 'Mtto del mes en curso',                    semanas: [w(2300, 4800), w(12900, 31600), w(3500, 13700), w(2400, 1200), w(0, 3000)] },
  { nombre: 'Mtto de meses anteriores (mora recuperada)', semanas: [w(0, 1200), w(1800, 8400), w(600, 3000), w(0, 2400), w(2400, 3000)] },
  { nombre: 'Mtto de meses futuros (cuotas adelantadas)', semanas: [w(5500, 0), w(3000, 18500), w(6400, 8500), w(4200, 2700), w(1200, 4200)] },
  { nombre: 'Aportación por Vinte',                     semanas: [w(0, 0), w(0, 0), w(0, 249999.99), w(0, 0), w(0, 0)] },
  { nombre: 'Monto por sanción',                        semanas: [w(0, 60), w(180, 470), w(300, 480), w(180, 240), w(240, 600)] },
  { nombre: 'Monto no identificada',                    semanas: [w(0, 0), w(0, 1800), w(0, 600), w(0, 0), w(0, 0)] },
  { nombre: 'Sanción de no identificados',              semanas: [w(0, 0), w(0, 0), w(0, 0), w(0, 0), w(0, 0)] },
  { nombre: 'Mejoras, sensor',                          semanas: [w(0, 0), w(0, 0), w(0, 540), w(0, 60), w(0, 180)] },
  { nombre: 'Mejoras, tag',                             semanas: [w(0, 0), w(0, 0), w(0, 1320), w(0, 630), w(0, 570)] },
  { nombre: 'Mejoras, internas',                        semanas: [w(0, 0), w(0, 0), w(0, 0), w(0, 0), w(0, 0)] },
  { nombre: 'Otros ingresos (FR)',                      semanas: [w(0, 0), w(0, 0), w(0, 0), w(0, 0), w(0, 0)] },
];

const FONDO_RESERVA: Concepto[] = [
  { nombre: 'FR de meses anteriores', semanas: [w(0, 0), w(0, 0), w(0, 0), w(0, 0), w(0, 0)] },
  { nombre: 'FR mes en curso',        semanas: [w(3600, 0), w(1800, 0), w(10800, 1800), w(10800, 1800), w(3600, 3600)] },
  { nombre: 'Otros ingresos (MT)',    semanas: [w(0, 600), w(0, 1200), w(0, 0), w(0, 0), w(0, 0)] },
];

// Viviendas entregadas que pagaron su FR esa semana, por tarjeta / depósito.
const VIVIENDAS: WeekVal[] = [w(2, 0), w(1, 0), w(6, 1), w(6, 1), w(2, 2)];
const EFECTIVO_EN_TRANSITO = 12300.0;

// ─── Cálculos ────────────────────────────────────────────────────────────────
const sumConceptos = (cs: Concepto[]): WeekVal[] =>
  SEMANAS.map((_, i) => ({
    tarjeta: cs.reduce((a, c) => a + c.semanas[i].tarjeta, 0),
    deposito: cs.reduce((a, c) => a + c.semanas[i].deposito, 0),
  }));

const totalOf = (sem: WeekVal[]) => sem.reduce((a, x) => a + x.tarjeta + x.deposito, 0);

const fmtMoney = (n: number) => (n === 0 ? '' : n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtCount = (n: number) => (n === 0 ? '' : String(n));

// Anchos
const LABEL_W = 250;
const TAR_W = 92;
const DEP_W = 100;
const WEEK_W = TAR_W + DEP_W;
const TOTAL_W = 120;

type Variant = 'concepto' | 'subtotal' | 'grand';

function MatrixRow({
  label, semanas, total, fmt = fmtMoney, variant = 'concepto',
}: {
  label: string; semanas: WeekVal[]; total: number; fmt?: (n: number) => string; variant?: Variant;
}) {
  const grand = variant === 'grand';
  const subtotal = variant === 'subtotal';
  const rowBg = grand ? `bg-secondary` : subtotal ? `bg-secondary/10` : ``;
  const labelStyle = grand ? `text-white font-700` : subtotal ? `text-secondary font-700` : `text-gray-700`;
  const cellStyle = grand ? `text-white font-700` : subtotal ? `text-secondary font-600` : `text-gray-700`;

  return (
    <View style={tw.style(`row border-b border-gray-100`, rowBg)}>
      <View style={tw.style(`px-3 py-2 justify-center border-r border-gray-100`, { width: LABEL_W })}>
        <Text style={tw.style(`text-xs`, labelStyle)} numberOfLines={2}>{label}</Text>
      </View>

      {semanas.map((wv, i) => (
        <React.Fragment key={i}>
          <View style={tw.style(`px-2 py-2 justify-center items-end`, { width: TAR_W })}>
            <Text style={tw.style(`text-xs`, cellStyle)}>{fmt(wv.tarjeta)}</Text>
          </View>
          <View style={tw.style(`px-2 py-2 justify-center items-end border-r border-gray-100`, { width: DEP_W })}>
            <Text style={tw.style(`text-xs`, cellStyle)}>{fmt(wv.deposito)}</Text>
          </View>
        </React.Fragment>
      ))}

      <View style={tw.style(`px-2 py-2 justify-center items-end bg-blue-50/40`, { width: TOTAL_W }, grand && `bg-secondary`)}>
        <Text style={tw.style(`text-xs font-700`, grand ? `text-white` : `text-gray-900`)}>{total === 0 ? '' : fmt(total)}</Text>
      </View>
    </View>
  );
}

export default function IngresosMensual({ communityId, date }: { communityId?: string; date?: Date }) {
  const mantSub = sumConceptos(MANTENIMIENTO);
  const frSub = sumConceptos(FONDO_RESERVA);
  const cobranza: WeekVal[] = SEMANAS.map((_, i) => ({
    tarjeta: mantSub[i].tarjeta + frSub[i].tarjeta,
    deposito: mantSub[i].deposito + frSub[i].deposito,
  }));
  const viviendasTotal = VIVIENDAS.reduce((a, x) => a + x.tarjeta + x.deposito, 0);

  return (
    <View style={tw`px-4 pb-10`}>
      <View style={tw`items-center mb-4`}>
        <Text style={tw`text-lg font-700 text-secondary`}>{COMMUNITY} — Reporte general de ingresos</Text>
        <Text style={tw`text-gray-400 text-xs`}>Detallado mensual · {PERIODO} · semanas inician en domingo</Text>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator style={tw`bg-white rounded-xl border border-gray-100`}>
        <View>
          {/* Cabecera superior: semanas */}
          <View style={tw`row`}>
            <View style={tw.style({ width: LABEL_W })} />
            {SEMANAS.map(s => (
              <View key={s.label} style={tw.style(`px-2 py-1.5 items-center justify-center bg-secondary border-r border-white`, { width: WEEK_W })}>
                <Text style={tw`text-white font-700 text-xs`}>{s.label}</Text>
                <Text style={tw`text-white/70 text-[10px]`}>{s.rango}</Text>
              </View>
            ))}
            <View style={tw.style(`items-center justify-center bg-secondary`, { width: TOTAL_W })}>
              <Text style={tw`text-white font-700 text-xs`}>TOTAL</Text>
            </View>
          </View>

          {/* Cabecera de columnas: Tarjeta / Depósito */}
          <View style={tw`row border-b border-gray-300 bg-secondary/10`}>
            <View style={tw.style(`px-3 py-2 border-r border-gray-200`, { width: LABEL_W })}>
              <Text style={tw`text-secondary font-600 text-xs`}>Concepto</Text>
            </View>
            {SEMANAS.map(s => (
              <React.Fragment key={s.label}>
                <View style={tw.style(`px-2 py-2 items-end`, { width: TAR_W })}><Text style={tw`text-secondary font-600 text-[11px]`}>Tarjeta</Text></View>
                <View style={tw.style(`px-2 py-2 items-end border-r border-gray-200`, { width: DEP_W })}><Text style={tw`text-secondary font-600 text-[11px]`}>Depósito</Text></View>
              </React.Fragment>
            ))}
            <View style={tw.style({ width: TOTAL_W })} />
          </View>

          {/* Sección Mantenimiento */}
          {MANTENIMIENTO.map(c => (
            <MatrixRow key={c.nombre} label={c.nombre} semanas={c.semanas} total={totalOf(c.semanas)} />
          ))}
          <MatrixRow label="Aportaciones de mantenimiento" semanas={mantSub} total={totalOf(mantSub)} variant="subtotal" />

          {/* Sección Fondo de Reserva */}
          <View style={tw`h-2`} />
          {FONDO_RESERVA.map(c => (
            <MatrixRow key={c.nombre} label={c.nombre} semanas={c.semanas} total={totalOf(c.semanas)} />
          ))}
          <MatrixRow label="Aportaciones de fondo de reserva" semanas={frSub} total={totalOf(frSub)} variant="subtotal" />

          {/* Gran total */}
          <MatrixRow label="Ingresos reportados por cobranza" semanas={cobranza} total={totalOf(cobranza)} variant="grand" />

          {/* Pie: viviendas entregadas con aportación (conteos) */}
          <View style={tw`h-2`} />
          <MatrixRow label="Viviendas entregadas y con aportación" semanas={VIVIENDAS} total={viviendasTotal} fmt={fmtCount} variant="subtotal" />

          {/* Pie: efectivo en tránsito */}
          <View style={tw`row border-t border-gray-200`}>
            <View style={tw.style(`px-3 py-2 justify-center items-end`, { width: LABEL_W + WEEK_W * SEMANAS.length })}>
              <Text style={tw`text-gray-600 font-600 text-xs`}>Efectivo en tránsito</Text>
            </View>
            <View style={tw.style(`px-2 py-2 justify-center items-end bg-blue-50/40`, { width: TOTAL_W })}>
              <Text style={tw`text-gray-900 font-700 text-xs`}>{fmtMoney(EFECTIVO_EN_TRANSITO)}</Text>
            </View>
          </View>
        </View>
      </ScrollView>

      {/* <Text style={tw`text-gray-300 text-xs mt-3`}>
        Datos demo · subtotales/totales calculados · concepto = clasificador por reglas · pendiente de backend · communityId: {communityId ?? '—'}
      </Text> */}
    </View>
  );
}
